import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getProvidersFromCache } from '../../utils/serviceCalls/cache';
import { getStripeData } from '../../utils/serviceCalls/stripe';

/** @typedef {import('../../utils/serviceCalls/cache').CacheItem} CacheItem */
/** @typedef {import('../Auth').AuthUser} AuthUser */
/** @typedef {import('../Auth').UserCategory} UserCategory */

/**
 * @typedef {'missing_session' | 'cache_not_found' | 'cache_error' | null} CheckoutDataError
 */

/**
 * @typedef {Object} CheckoutDataState
 * @property {CacheItem | null} data - The checkout data (cache or provider data)
 * @property {boolean} isLoading - Whether data is currently being loaded
 * @property {CheckoutDataError} error - Error state if data loading failed
 */

/**
 * Custom hook to load checkout data based on user type.
 *
 * ADMIN FLOW:
 * - Requires a `session` query parameter
 * - Fetches provider data from the /cache endpoint
 * - Returns error if session is missing or cache is not found
 *
 * PROVIDER FLOW:
 * - Does not use session or cache endpoint
 * - Builds checkout data directly from authenticated user info
 * - Returns data immediately (no async loading)
 *
 * @param {Object} params
 * @param {AuthUser} params.user - Authenticated user data
 * @param {boolean} params.isAdmin - Whether the user is an admin
 * @returns {CheckoutDataState}
 */
export const useCheckoutData = ({ user, isAdmin }) => {
  const [searchParams] = useSearchParams();

  const [data, setData] = useState(/** @type {CacheItem | null} */ (null));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(/** @type {CheckoutDataError} */ (null));

  const { userId, pwid, fullName, email } = user;

  const session = searchParams.get('session');

  useEffect(() => {
    /**
     * Loads checkout data for admin users from the cache endpoint
     */

    const loadAdminData = async () => {
      // Extract query parameters
      const sessionId = searchParams.get('session_id');
      const fromSource = searchParams.get('fromSource');

      // IMPORTANT: If we're coming from Stripe redirect (has session_id),
      // skip cache loading. The verification process will handle the flow.
      // This prevents showing "Unable to Load Checkout" error when returning from Stripe.
      if (sessionId && fromSource === 'self-serve-success') {
        // Don't set error - let the verification useEffect handle it
        return;
      }

      if (!session) {
        // Admin users must have a session parameter (unless coming from Stripe)
        setError('missing_session');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const cacheResponse = await getProvidersFromCache(session);

        // Check if cache was found and loaded successfully
        if (!cacheResponse?.success || !cacheResponse?.found) {
          setError('cache_not_found');
          return;
        }

        setData(cacheResponse.cacheItem);
      } catch (err) {
        console.error('Failed to load cache data:', err);
        setError('cache_error');
      } finally {
        setIsLoading(false);
      }
    };

    /**
     * Builds checkout data for provider users from their auth info
     */
    const buildProviderData = () => {
      const providerData = {
        userId,
        providerSelectionCacheId: '',
        selectedProviders: pwid
          ? [
              {
                pwid,
                providerName: fullName,
                email,
                last4: '',
              },
            ]
          : [],
      };

      setData(providerData);
    };

    // Route to the appropriate data loading function
    if (isAdmin) {
      loadAdminData();
    } else {
      buildProviderData();
    }
    // Only depend on primitive values, not the user object reference
  }, [isAdmin, userId, pwid, fullName, email, session, searchParams.get]);

  return { data, isLoading, error };
};

/**
 * Custom hook to fetch and manage Stripe publishable key
 *
 * This hook fetches the Stripe configuration from the backend and returns
 * the publishable key if Stripe is enabled.
 *
 * @returns {{
 *   publishableKey: string | null,
 *   isLoading: boolean,
 *   error: boolean
 * }} Stripe publishable key state
 */
export const useStripePublishableKey = () => {
  const [publishableKey, setPublishableKey] = useState(
    /** @type {string | null} */ (null),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchStripeData = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const stripeData = await getStripeData();

        if (!stripeData) {
          setError(true);
          return;
        }

        // Only set publishable key if Stripe is enabled
        if (stripeData.isStripeEnabled?.toLowerCase() === 'true') {
          setPublishableKey(stripeData.publishableKey || null);
        } else {
          setPublishableKey(null);
        }
      } catch (err) {
        console.error('Failed to fetch Stripe data:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStripeData();
  }, []);

  return { publishableKey, isLoading, error };
};
